<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    location="bottom start"
    offset="8"
    transition="scale-transition"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        class="company-selector__trigger text-none"
        :class="{ 'is-open': menu }"
        variant="flat"
        rounded="pill"
        height="40"
      >
        <v-icon start size="20" class="company-selector__icon">mdi-office-building-marker</v-icon>
        <span class="company-selector__summary">{{ summary }}</span>
        <span v-if="extraCount" class="company-selector__badge">+{{ extraCount }}</span>
        <v-icon end size="20" class="company-selector__chevron">
          {{ menu ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </v-btn>
    </template>

    <v-card
      class="company-selector__card"
      width="360"
      elevation="12"
      rounded="lg"
      @keydown.enter.prevent.stop="apply"
    >
      <div class="pa-3 pb-2 d-flex align-center" style="gap: 8px;">
        <v-text-field
          v-model="search"
          class="flex-grow-1"
          density="compact"
          variant="solo-filled"
          flat
          hide-details
          rounded="lg"
          clearable
          placeholder="Search company…"
          prepend-inner-icon="mdi-magnify"
          autofocus
          @keydown.enter.prevent="apply"
        />
        <v-btn
          class="company-selector__apply text-none flex-shrink-0"
          color="primary"
          variant="flat"
          rounded="lg"
          height="40"
          @click="apply"
        >
          Apply
        </v-btn>
      </div>

      <v-list class="company-selector__list" density="comfortable" lines="two" select-strategy="independent">
        <!-- Todas las compañías -->
        <v-list-item @click="toggleAll" rounded="lg" class="mx-2">
          <template #prepend>
            <v-checkbox-btn
              :model-value="allSelected"
              :indeterminate="someSelected && !allSelected"
              color="primary"
              density="compact"
              @click.stop="toggleAll"
            />
          </template>
          <v-list-item-title class="font-weight-medium">All companies</v-list-item-title>
          <v-list-item-subtitle>{{ items.length }} companies in your holding</v-list-item-subtitle>
        </v-list-item>

        <v-divider class="my-1" />

        <!-- Listado -->
        <template v-if="filtered.length">
          <v-list-item
            v-for="c in filtered"
            :key="c.uuid"
            @click="toggle(c.uuid)"
            rounded="lg"
            class="mx-2"
            lines="one"
          >
            <template #prepend>
              <v-checkbox-btn
                :model-value="isSelected(c.uuid)"
                color="primary"
                density="compact"
                @click.stop="toggle(c.uuid)"
              />
            </template>
            <v-list-item-title>{{ c.name }}</v-list-item-title>
            <template #append v-if="c.uuid === own_uuid">
              <span class="company-selector__own">Your company</span>
            </template>
          </v-list-item>
        </template>
        <v-list-item v-else lines="one" class="text-medium-emphasis">
          <v-list-item-title>No companies match “{{ search }}”</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup>
import { useSessionStore } from '@/stores/session'

const session_store = useSessionStore()

const menu = ref(false)
const search = ref('')

const own_uuid = computed(() => session_store.my_company?.uuid)

// Compañía propia primero, resto ordenadas por nombre.
const items = computed(() => {
  const list = session_store.selectable_companies.slice()
  return list.sort((a, b) => {
    if (a.uuid === own_uuid.value) return -1
    if (b.uuid === own_uuid.value) return 1
    return a.name.localeCompare(b.name)
  })
})

const filtered = computed(() => {
  const q = (search.value || '').trim().toLowerCase()
  if (!q) return items.value
  return items.value.filter(c => c.name.toLowerCase().includes(q))
})

// Selección APLICADA (la del store) — alimenta el resumen del botón.
const active = computed(() => session_store.active_companies)

// Selección TEMPORAL (draft): las casillas la modifican; solo se confirma con Apply.
const draft = ref([])

// Al abrir el menú, sincroniza el draft con lo aplicado actualmente.
watch(menu, (open) => {
  if (open) draft.value = [...active.value]
})

// Si la selección aplicada cambia mientras el menú está cerrado (p. ej. init a la
// compañía propia, o reset externo), mantener el draft alineado para no aplicar
// una selección obsoleta al reabrir.
watch(active, (val) => {
  if (!menu.value) draft.value = [...val]
}, { immediate: true })

// Estado de las casillas (basado en el draft).
function isSelected(uuid) {
  return draft.value.includes(uuid)
}
const allSelected = computed(() => items.value.length > 0 && draft.value.length === items.value.length)
const someSelected = computed(() => draft.value.length > 0)

function toggle(uuid) {
  draft.value = isSelected(uuid)
    ? draft.value.filter(u => u !== uuid)
    : [...draft.value, uuid]
}

function toggleAll() {
  draft.value = allSelected.value
    ? (own_uuid.value ? [own_uuid.value] : [])
    : items.value.map(c => c.uuid)
}

// Confirma el draft y cierra (Enter o botón Apply).
function apply() {
  session_store.setActiveCompanies(draft.value)
  menu.value = false
}

// Resumen / badge del botón: reflejan lo APLICADO, no el draft.
const appliedAll = computed(() => items.value.length > 0 && active.value.length === items.value.length)
const summary = computed(() => {
  if (active.value.length === 0) return 'Select company'
  if (active.value.length === 1) {
    const c = items.value.find(i => i.uuid === active.value[0]) || session_store.my_company
    return c ? c.name : '1 company'
  }
  if (appliedAll.value) return `All companies · ${items.value.length}`
  const first = items.value.find(i => i.uuid === active.value[0])
  return first ? first.name : `${active.value.length} companies`
})

// Nº de compañías extra (para el badge "+N" del botón).
const extraCount = computed(() =>
  active.value.length > 1 && !appliedAll.value ? active.value.length - 1 : 0
)
</script>

<style scoped>
/* ===== Píldora de cristal con borde neón (theme-aware) ===== */
.company-selector__trigger {
  backdrop-filter: blur(10px);
  min-width: 240px;
  max-width: 360px;
  justify-content: flex-start;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding-inline: 14px;
  border: 1px solid rgba(34, 211, 238, 0.55);
  box-shadow:
    0 0 0 1px rgba(99, 102, 241, 0.18),
    0 0 14px rgba(34, 211, 238, 0.3),
    inset 0 0 14px rgba(34, 211, 238, 0.05);
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.2s ease;
}

.v-theme--dark .company-selector__trigger {
  background: rgba(13, 19, 33, 0.66) !important;
  color: #e6f6ff !important;
}

.v-theme--light .company-selector__trigger {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #0f172a !important;
}

.company-selector__trigger:hover {
  border-color: rgba(34, 211, 238, 0.95);
  box-shadow:
    0 0 18px rgba(34, 211, 238, 0.55),
    0 0 30px rgba(168, 85, 247, 0.35);
  transform: translateY(-1px);
}

.company-selector__trigger.is-open {
  border-color: #22d3ee;
  box-shadow:
    0 0 20px rgba(34, 211, 238, 0.7),
    0 0 34px rgba(168, 85, 247, 0.4);
}

.company-selector__icon {
  filter: drop-shadow(0 0 6px rgba(34, 211, 238, 0.7));
}

.v-theme--dark .company-selector__icon { color: #22d3ee; }
.v-theme--light .company-selector__icon { color: #0891b2; }

.company-selector__summary {
  flex: 1 1 auto;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-inline-start: 6px;
}

.v-theme--dark .company-selector__summary {
  text-shadow: 0 0 8px rgba(125, 211, 252, 0.25);
}

.v-theme--dark .company-selector__chevron { color: #7dd3fc; opacity: 0.85; }
.v-theme--light .company-selector__chevron { color: #0e7490; opacity: 0.9; }

/* Badge "+N" neón degradado (igual en ambos temas) */
.company-selector__badge {
  margin-inline-start: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  color: #04121f;
  background: linear-gradient(90deg, #22d3ee, #a855f7);
  box-shadow: 0 0 10px rgba(34, 211, 238, 0.6);
}

/* ===== Menú desplegable (theme-aware) ===== */
.company-selector__card {
  background: rgb(var(--v-theme-surface)) !important;
  border: 1px solid rgba(56, 189, 248, 0.22);
  backdrop-filter: blur(12px);
}

.v-theme--dark .company-selector__card {
  background: rgba(11, 16, 28, 0.96) !important;
  box-shadow:
    0 18px 50px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(99, 102, 241, 0.12),
    0 0 26px rgba(34, 211, 238, 0.12);
}

.v-theme--light .company-selector__card {
  box-shadow:
    0 18px 44px rgba(15, 23, 42, 0.18),
    0 0 0 1px rgba(34, 211, 238, 0.08);
}

.company-selector__list {
  max-height: 320px;
  overflow-y: auto;
  padding-bottom: 6px;
  background: transparent;
}

/* Item activo / hover con tinte neón */
.company-selector__list :deep(.v-list-item--active),
.company-selector__list :deep(.v-list-item:hover) {
  background: linear-gradient(90deg, rgba(34, 211, 238, 0.14), rgba(168, 85, 247, 0.08));
}

.company-selector__list :deep(.v-list-item--active) {
  box-shadow: inset 3px 0 0 0 #22d3ee;
}

/* Checkboxes en neón */
.company-selector__list :deep(.v-selection-control--dirty .v-icon) {
  color: #22d3ee !important;
  filter: drop-shadow(0 0 5px rgba(34, 211, 238, 0.7));
}

/* Etiqueta "Your company" neón */
.company-selector__own {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid rgba(34, 211, 238, 0.5);
  background: rgba(34, 211, 238, 0.1);
  box-shadow: 0 0 8px rgba(34, 211, 238, 0.25);
}

.v-theme--dark .company-selector__own { color: #22d3ee; }
.v-theme--light .company-selector__own { color: #0e7490; }

/* Buscador */
.company-selector__card :deep(.v-field) {
  border: 1px solid rgba(56, 189, 248, 0.18);
}

.v-theme--dark .company-selector__card :deep(.v-field) {
  background: rgba(34, 211, 238, 0.06) !important;
}

.v-theme--light .company-selector__card :deep(.v-field) {
  background: rgba(34, 211, 238, 0.05) !important;
}

.company-selector__card :deep(.v-field--focused) {
  border-color: rgba(34, 211, 238, 0.7);
  box-shadow: 0 0 12px rgba(34, 211, 238, 0.35);
}
</style>
